import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Author, GET_TINYCMS_AUTHORS } from '../cms/authors';

export { structuredDataLine } from '../cms/authors';

/**
 * The article's byline (Prototype v17, backlog 53b14acc / 080f7fe8): who
 * wrote it, under the title and in Classification, and the Byline editor while
 * editing — the author, co-authors (each named after the author and linked to
 * their page on the site), and whether to show an "Updated" date. A byline is
 * part of the article, so it goes live with the article's next approval.
 */

/** The site's authors, by id (none before any are added). */
export function useSiteAuthors(accountId: string | null | undefined) {
  const { data } = useQuery<{ tinycmsAuthors: Author[] }>(GET_TINYCMS_AUTHORS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const list = data?.tinycmsAuthors || [];
  return { list, byId: new Map(list.map((a) => [a.id, a])) };
}

const authorLink = (id: string) => `/content?view=authors&author=${id}`;

/**
 * "By Hai Tran and Dana Ruiz", each name opening that author in Content →
 * Authors; an old free-text byline as written; "+ Add author" or "No author"
 * (amber) when there is none. Nothing while a byline's authors are still loading.
 */
export function BylineNames({
  ids,
  byId,
  legacy,
  prefix = true,
  onAdd,
}: {
  ids: string[];
  byId: Map<string, Author>;
  legacy?: string | null;
  /** "By " first, as under the title; Classification lists names alone. */
  prefix?: boolean;
  /** With no author: a control to add one, where it's fixed (backlog b23a8dd3). */
  onAdd?: () => void;
}) {
  const people = ids.map((id) => byId.get(id)).filter((a): a is Author => Boolean(a));
  const by = prefix ? 'By ' : '';
  if (ids.length && !people.length) return null;
  if (!people.length) {
    return legacy ? (
      <>
        {by}
        <b style={{ fontWeight: 500 }}>{legacy}</b>
      </>
    ) : onAdd ? (
      <button type="button" className="ink-byline-add" onClick={onAdd}>
        + Add author
      </button>
    ) : (
      <Link to="/content?view=authors" className="ink-byline-none">
        No author
      </Link>
    );
  }
  return (
    <>
      {by}
      {people.map((a, i) => (
        <React.Fragment key={a.id}>
          {i === 0 ? '' : i === people.length - 1 ? ' and ' : ', '}
          <Link to={authorLink(a.id)} className="ink-byline-name">
            {a.name}
          </Link>
        </React.Fragment>
      ))}
    </>
  );
}

export function BylineEditor({
  authors,
  ids,
  onChange,
  showUpdated,
  onShowUpdated,
  legacy,
  onClearLegacy,
  publishedLine,
}: {
  authors: Author[];
  ids: string[];
  onChange: (ids: string[]) => void;
  showUpdated: boolean;
  onShowUpdated: (v: boolean) => void;
  /** An old free-text byline, shown until it's replaced. */
  legacy: string;
  onClearLegacy: () => void;
  /** "Oct 10, 2026 · 2:35 PM, when it first went live" / "Set automatically when you publish". */
  publishedLine: string;
}) {
  const [primary, ...co] = ids;
  const main = primary ? authors.find((a) => a.id === primary) : null;
  const coAuthors = co
    .map((id) => authors.find((a) => a.id === id))
    .filter((a): a is Author => Boolean(a));
  const addable = authors.filter((a) => a.id !== primary && !co.includes(a.id));
  return (
    <div className="ink-edit-frame ink-byline-edit" data-screen-label="Byline">
      <div className="ink-edit-frame-label">
        BYLINE · shown under the headline, with the published date
      </div>
      {authors.length ? (
        <div className="ink-byline-grid">
          <label className="ink-byline-field">
            <span>Author</span>
            <select
              className="ink-input"
              value={primary || ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange(v ? [v, ...co.filter((c) => c !== v)] : []);
              }}
            >
              <option value="">No author</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <div className="ink-byline-field">
            <span>Published date</span>
            <span className="ink-byline-date">{publishedLine}</span>
          </div>
          <div className="ink-byline-field ink-byline-wide">
            <span>
              Co-authors{' '}
              <span className="ink-muted">
                · named in the byline after the author, each linked to their page
              </span>
            </span>
            <div className="ink-byline-chips">
              {coAuthors.map((a) => (
                <span key={a.id} className="ink-byline-chip">
                  {a.name}
                  <button
                    type="button"
                    aria-label={`Remove ${a.name} as co-author`}
                    onClick={() => onChange([primary, ...co.filter((c) => c !== a.id)])}
                  >
                    ×
                  </button>
                </span>
              ))}
              {primary && addable.length ? (
                <select
                  className="ink-input ink-byline-add"
                  value=""
                  onChange={(e) => e.target.value && onChange([primary, ...co, e.target.value])}
                >
                  <option value="">+ Add co-author</option>
                  {addable.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              ) : null}
              {!primary ? <span className="ink-muted">Pick an author first.</span> : null}
            </div>
          </div>
        </div>
      ) : (
        <p className="ink-muted" style={{ margin: '6px 0 0' }}>
          No authors yet. Ask your agent to add them, or see{' '}
          <Link to="/content?view=authors">Website → Authors</Link>.
        </p>
      )}
      {legacy && !ids.length ? (
        <p className="ink-muted" style={{ margin: '8px 0 0' }}>
          Old byline text: “{legacy}”, not linked to an author page.{' '}
          <button type="button" className="ink-quiet-link" onClick={onClearLegacy}>
            Remove it
          </button>
        </p>
      ) : null}
      <div className="ink-byline-foot">
        <button
          type="button"
          className="ink-byline-toggle"
          role="switch"
          aria-checked={showUpdated}
          onClick={() => onShowUpdated(!showUpdated)}
        >
          <span className="ink-switch" data-on={showUpdated} />
          Show an “Updated” date when a new version goes live
        </button>
        <Link to={main ? authorLink(main.id) : '/content?view=authors'} className="ink-quiet-link">
          {main ? `Edit ${main.name.split(' ')[0]}’s profile →` : 'Manage authors →'}
        </Link>
      </div>
    </div>
  );
}
